import { useState } from 'react';

import {
  PROJECT_IMPACT_CHANGED,
  type LineClassificationDto,
  type MeResponse,
  type ProjectDto,
} from '@time-tracking/shared';

import { ApiError } from '../../api/client.js';
import type { ToastKind } from '../../components/useToasts.js';
import {
  useArchiveProject,
  useLockProject,
  usePatchProject,
  useProjectArchiveImpact,
  useProjectMembers,
  useRestoreProject,
} from '../../api/projects.js';
import { ArchiveProjectDialog } from './dialogs/ArchiveProjectDialog.js';
import { EditProjectDialog } from './dialogs/EditProjectDialog.js';
import { LockProjectDialog } from './dialogs/LockProjectDialog.js';
import { ActivityPane } from './panes/ActivityPane.js';
import { MembersPane } from './panes/MembersPane.js';
import { TasksPane } from './panes/TasksPane.js';
import { TypesPane } from './panes/TypesPane.js';

type TeamOption = MeResponse['teams'][number];
type Tab = 'tasks' | 'members' | 'types' | 'activity';

const TABS: { id: Tab; label: string }[] = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'members', label: 'Members' },
  { id: 'types', label: 'Types' },
  { id: 'activity', label: 'Activity' },
];

interface ProjectDetailProps {
  project: ProjectDto;
  classifications: LineClassificationDto[];
  teams: TeamOption[];
  onToast: (msg: string, kind: ToastKind) => void;
}

export function ProjectDetail({ project, classifications, teams, onToast }: ProjectDetailProps) {
  const [activeTab, setActiveTab] = useState<Tab>('tasks');
  const [showEdit, setShowEdit] = useState(false);
  const [showLock, setShowLock] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  const membersQuery = useProjectMembers(activeTab === 'members' ? project.projectId : null);
  const patchMutation = usePatchProject();
  const lockMutation = useLockProject();
  const archiveMutation = useArchiveProject();
  const restoreMutation = useRestoreProject();
  // Fetched only while the dialog is open: it is confirm-time copy, not header
  // data, and it walks the project's whole timesheet-line history.
  const archiveImpactQuery = useProjectArchiveImpact(showArchive ? project.projectId : null);

  const isArchived = project.archivedAt !== null;

  const projectTeam = teams.find((t) => t.teamId === project.teamId);

  function handleTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, idx: number) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (idx + 1) % TABS.length;
      const nextTab = TABS[next];
      if (nextTab) {
        setActiveTab(nextTab.id);
        document.getElementById(`detail-tab-${nextTab.id}`)?.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (idx - 1 + TABS.length) % TABS.length;
      const prevTab = TABS[prev];
      if (prevTab) {
        setActiveTab(prevTab.id);
        document.getElementById(`detail-tab-${prevTab.id}`)?.focus();
      }
    }
  }

  function handleEdit(patch: { name: string; code: string; description: string | null }) {
    patchMutation.mutate(
      { projectId: project.projectId, patch },
      {
        onSuccess: () => {
          setShowEdit(false);
          onToast('Project updated', 'success');
        },
        onError: (err) => onToast(err.message, 'error'),
      },
    );
  }

  function handleLock(lockedAt: string) {
    lockMutation.mutate(
      { projectId: project.projectId, body: { lockedAt } },
      {
        onSuccess: () => {
          setShowLock(false);
          onToast('Project locked', 'success');
        },
        onError: (err) => onToast(err.message, 'error'),
      },
    );
  }

  function handleArchiveConfirm({
    releaseCode,
    observedLoggedMinutes,
  }: {
    releaseCode: boolean;
    observedLoggedMinutes: number;
  }) {
    if (isArchived) {
      restoreMutation.mutate(
        { projectId: project.projectId },
        {
          onSuccess: () => {
            setShowArchive(false);
            onToast(`"${project.name}" restored`, 'success');
          },
          onError: (err) => onToast(err.message, 'error'),
        },
      );
      return;
    }
    archiveMutation.mutate(
      { projectId: project.projectId, body: { releaseCode, observedLoggedMinutes } },
      {
        onSuccess: (updated) => {
          setShowArchive(false);
          // Archive is idempotent by STATE, so a project another manager archived
          // a moment ago answers 200 with the work already done — including the
          // part this caller asked for and did not get. Releasing the code is the
          // only such part, and the response already tells us: if the project
          // still holds the code we were asked to free, the archive that landed
          // was not the one that frees it.
          //
          // Reported rather than retried. The manager who archived first chose
          // not to free the code, and overriding that from a stale dialog is
          // worse than saying so. Comparing against `project.code` — the exact
          // string the checkbox offered to free — is also what keeps a genuine
          // retry quiet: there, the code really did change.
          if (releaseCode && updated.code === project.code) {
            onToast(
              `"${project.name}" was already archived, so the code ${project.code} was not freed and is still in use.`,
              'warn',
            );
            return;
          }
          onToast(`"${project.name}" archived`, 'success');
        },
        onError: (err) => {
          // The impact went stale under the open dialog and the server refused
          // rather than freeing a code that now sits on historical report rows.
          // Nothing was archived, so the dialog STAYS OPEN and refetches: the
          // manager sees the hours appear and the caution with them, and decides
          // again. Closing here would hide the reason and leave them to wonder
          // whether the archive landed.
          if (err instanceof ApiError && err.code === PROJECT_IMPACT_CHANGED) {
            void archiveImpactQuery.refetch();
          }
          onToast(err.message, 'error');
        },
      },
    );
  }

  return (
    <div className="projects-detail">
      <div className="project-detail-header">
        <div className="project-detail-header__top">
          <div className="project-detail-header__left">
            <div className="project-detail-header__title-row">
              <h2 className="project-detail-header__name">{project.name}</h2>
              <span className="project-code-chip" aria-label={`Project code: ${project.code}`}>
                {project.code}
              </span>
              {projectTeam && (
                <span className="project-team-chip" aria-label={`Team: ${projectTeam.name}`}>
                  <span className="dot" aria-hidden="true" />
                  {projectTeam.name}
                </span>
              )}
              {project.lockedAt && (
                <span
                  className="project-state-pill project-state-pill--locked"
                  aria-label={`Locked since ${project.lockedAt}`}
                  style={{ gap: 4 }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M9 5H8V3.5a2 2 0 0 0-4 0V5H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm-3 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM7 5H5V3.5a1 1 0 0 1 2 0V5z" />
                  </svg>
                  Locked
                </span>
              )}
              {isArchived && (
                <span
                  className="project-state-pill project-state-pill--archived-state"
                  aria-label="This project is archived"
                  style={{ gap: 4 }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M1 2.5A.5.5 0 0 1 1.5 2h9a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-1zM2 5h8v4.5a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5V5zm2.5 1.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z" />
                  </svg>
                  Archived
                </span>
              )}
            </div>

            {(project.leads.length > 0 || project.description) && (
              <div className="project-detail-header__meta">
                {project.leads.length > 0 && (
                  <span className="project-lead-line">
                    · {project.leads.length === 1 ? 'lead' : 'leads'}:{' '}
                    {project.leads.map((l) => l.displayName).join(', ')}
                  </span>
                )}
                {project.description && (
                  <span className="project-description" title={project.description}>
                    {project.description}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="project-detail-header__actions">
            {!project.lockedAt && (
              <button
                type="button"
                className="btn btn--ghost btn--sm project-action-btn"
                onClick={() => setShowLock(true)}
                aria-label="Lock project at cutoff date"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M9 5H8V3.5a2 2 0 0 0-4 0V5H3a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm-3 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM7 5H5V3.5a1 1 0 0 1 2 0V5z" />
                </svg>
                Lock at cutoff…
              </button>
            )}
            <button
              type="button"
              className="btn btn--sm project-action-btn"
              onClick={() => setShowEdit(true)}
              aria-label="Edit project details"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" />
              </svg>
              Edit
            </button>
            {/* Archive and restore share one button because they are the same
                affordance in two states. The label is the only thing that says
                which — hence the pill above, so the state is legible without
                reading a secondary control's verb. */}
            <button
              type="button"
              className="btn btn--ghost btn--sm project-action-btn"
              onClick={() => setShowArchive(true)}
              aria-label={isArchived ? 'Restore project' : 'Archive project'}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 12 12"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M1 2.5A.5.5 0 0 1 1.5 2h9a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5v-1zM2 5h8v4.5a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5V5zm2.5 1.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3z" />
              </svg>
              {isArchived ? 'Restore…' : 'Archive…'}
            </button>
          </div>
        </div>

        <div role="tablist" aria-label="Project details" className="detail-tabs">
          {TABS.map((tab, idx) => {
            const badge =
              tab.id === 'tasks' && project.taskCount > 0
                ? project.taskCount
                : tab.id === 'types' && classifications.length > 0
                  ? classifications.length
                  : null;
            return (
              <button
                key={tab.id}
                id={`detail-tab-${tab.id}`}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`detail-panel-${tab.id}`}
                className={`detail-tab${activeTab === tab.id ? ' detail-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, idx)}
                tabIndex={activeTab === tab.id ? 0 : -1}
                type="button"
              >
                {tab.label}
                {badge !== null && (
                  <span
                    className={`tab-badge${activeTab === tab.id ? ' tab-badge--active' : ''}`}
                    aria-hidden="true"
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id={`detail-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`detail-tab-${activeTab}`}
        className="detail-tab-content"
      >
        {activeTab === 'tasks' && (
          <TasksPane
            project={project}
            classifications={classifications}
            teams={teams}
            onToast={onToast}
          />
        )}
        {activeTab === 'members' && (
          <>
            {membersQuery.isLoading && (
              <div className="pane-loading" aria-live="polite">
                Loading members…
              </div>
            )}
            {membersQuery.error && (
              <div className="pane-error" role="alert">
                Failed to load members: {membersQuery.error.message}
              </div>
            )}
            {membersQuery.data && (
              <MembersPane
                projectId={project.projectId}
                leads={project.leads}
                paletteTeams={project.teams}
                members={membersQuery.data}
                isArchived={isArchived}
                onToast={onToast}
              />
            )}
          </>
        )}
        {activeTab === 'types' && (
          <TypesPane projectId={project.projectId} classifications={classifications} />
        )}
        {activeTab === 'activity' && <ActivityPane projectId={project.projectId} />}
      </div>

      <EditProjectDialog
        project={showEdit ? project : null}
        onClose={() => setShowEdit(false)}
        onSubmit={handleEdit}
        isPending={patchMutation.isPending}
      />

      <LockProjectDialog
        projectName={showLock ? project.name : null}
        open={showLock}
        onClose={() => setShowLock(false)}
        onSubmit={handleLock}
        isPending={lockMutation.isPending}
      />

      <ArchiveProjectDialog
        project={showArchive ? project : null}
        impact={archiveImpactQuery.data}
        // isFetching, not isLoading. isLoading is only true on a first load with
        // no cache, so reopening the dialog would show the PREVIOUS impact while
        // a refetch was still in flight — and confirm would already be enabled.
        // That is how a project which has since accrued hours gets the "free the
        // code" box defaulted ON from a stale loggedMinutes of 0, quietly
        // rewriting the code on every historical report row. Waiting for fresh
        // data is the whole reason the gate exists.
        impactLoading={archiveImpactQuery.isFetching}
        impactError={archiveImpactQuery.error !== null}
        onRetryImpact={() => void archiveImpactQuery.refetch()}
        onClose={() => setShowArchive(false)}
        onConfirm={handleArchiveConfirm}
        isPending={archiveMutation.isPending || restoreMutation.isPending}
      />
    </div>
  );
}
